/**
 * orgApi.js
 * Data source for the Org Chart — backed by the real HRMS hierarchy endpoint.
 *
 *   GET /saas/hrms/current-employment/hierarchy/{empId}/{reportingType}
 *   reportingType:
 *     "MeReportingTo" — the chain from empId up to the top-level manager.
 *     "ReportingToMe" — empId's direct reports or flattened downward subtree.
 *   Service code: HR02H.
 *
 * Ported into workplane-ui: this now goes through the app's shared HRMS
 * client (src/api/api.js -> hrmsGet), so it automatically carries the
 * signed-in user's Bearer token, X-Tenant-Code and X-Service-Code from
 * sessionDB — the same auth every other HRMS/ESS module uses. (The
 * standalone version hit `fetch()` directly with a hardcoded dev token.)
 *
 * Profiles use empname and base64 emp_pic from the hierarchy response.
 *
 * There's no endpoint yet for "everyone with no manager" (company-wide) or
 * for name/designation search across the whole company — those stay scoped
 * to whatever's already been fetched into the client-side cache built up
 * in orgTree.js. Swap those in once their endpoints exist.
 */

import { hrmsGet } from "../../../api/api";

const SERVICE_CODE = "HR02H";


function employeePhotoSource(value) {
  if (typeof value !== "string" || !value.trim()) return null;
  const base64 = value.replace(/\s/g, "");
  const formats = [["/9j/", "jpeg"], ["iVBORw0", "png"], ["R0lGOD", "gif"], ["UklGR", "webp"], ["Qk", "bmp"]];
  const format = formats.find(([prefix]) => base64.startsWith(prefix));
  return format ? "data:image/" + format[1] + ";base64," + base64 : null;
}

function normalizeEmployee(e) {
  if (!e) return null;
  return {
    empId: e.empId,
    name: e.empname ?? "",
    designation: e.designation,
    department: e.department,
    phone: e.phone ?? "",
    email: e.email ?? "",
    managerId: e.managerId,
    hrId: e.hrId,
    level: e.level,
    hasReports: e.hasReports === true,
    directReportCount: Number.isInteger(e.directReportCount) && e.directReportCount >= 0
      ? e.directReportCount : null,
    photoUrl: employeePhotoSource(e.emp_pic),
  };
}

async function getHierarchy(empId, reportingType) {
  // hrmsGet already prefixes HRMS_BASE_URL (`${API_BASE_URL}/saas/hrms`),
  // injects the auth headers, parses JSON and throws on a non-2xx status.
  const body = await hrmsGet(
    `/current-employment/hierarchy/${empId}/${reportingType}`,
    SERVICE_CODE
  );
  const data = body?.data ?? {};
  return {
    self: normalizeEmployee(data.employee),
    chain: (data.reportingChain ?? []).map(normalizeEmployee).filter(Boolean),
  };
}

/**
 * The chain from empId up to the top-level manager: [self, manager,
 * manager's manager, ..., top]. Fetched in full on every re-centre —
 * OrgChartPage.jsx's expand-up chevron just reveals more of this locally
 * (see getVisibleChain in orgTree.js), no extra round-trip needed.
 */
export async function fetchManagerChain(empId) {
  const { self, chain } = await getHierarchy(empId, "MeReportingTo");
  return [self, ...chain].filter(Boolean);
}

/** Fetch reports for this employee. Both direct-only and flattened subtree
 * responses are grouped by managerId by mergeReports. */
export async function fetchDirectReports(empId) {
  const { chain } = await getHierarchy(empId, "ReportingToMe");
  return chain;
}
